import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Investors from "@/pages/Investors";
import IFC from "@/pages/IFC";
import IfcCsc from "@/pages/IfcCsc";
import Ciro from "@/pages/Ciro";
import Courses from "@/pages/Courses";
import Games from "@/pages/Games";
import Podcasts from "@/pages/Podcasts";
import AudioBooks from "@/pages/AudioBooks";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import YouTube from "@/pages/YouTube";
import MutualFundBook from "@/pages/MutualFundBook";
import InvestmentAcademy from "@/pages/InvestmentAcademy";
import NewsAISwarm from "@/pages/NewsAISwarm";

// Structured data for the organization
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Harry Vadalkar",
  "url": "https://harryvadalkar.ca",
  "image": "https://customer-assets.emergentagent.com/job_e36899ae-c2fa-440f-bf36-c9469e111964/artifacts/f8pxlvyc_HarryVadalkar-Website-4.jpg",
  "sameAs": [
    "https://www.youtube.com/@FinanceinCanada",
    "https://www.linkedin.com/in/herambvadalkar/"
  ],
  "jobTitle": "Financial Educator & Author",
  "worksFor": {
    "@type": "Organization",
    "name": "SAIT & Mount Royal University"
  },
  "description": "Harvard & MIT certified financial educator, CFA, CSC, MBA. Author of Wealthy Canadian. Expert in Canadian investing, CIRO exam prep, CSC course, IFC exam preparation."
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Harry Vadalkar - Canadian Financial Education",
  "url": "https://harryvadalkar.ca",
  "description": "Learn investing in Canada with expert courses on CSC exam prep, IFC exam preparation, CIRO certification, and wealth building strategies.",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://harryvadalkar.ca/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

function App() {
  return (
    <HelmetProvider>
      <Helmet>
        <html lang="en-CA" />
        <title>Harry Vadalkar | Canadian Investing Courses | CSC Exam Prep | IFC Course | CIRO Certification</title>
        <meta name="description" content="Master Canadian investing with Harry Vadalkar. Expert CSC exam prep, IFC exam preparation, CIRO certification courses. Author of Wealthy Canadian book. 30,000+ students trained in personal finance, stock market investing, mutual funds, ETFs, TFSA, RRSP strategies." />
        <meta name="keywords" content="Canadian investing courses, CSC exam prep, CSC course Canada, Canadian Securities Course, IFC exam, IFC course, Investment Funds in Canada, CIRO exam prep, CIRO certification, CIRE exam, RSE exam, Harry Vadalkar, Wealthy Canadian book, investing in Canada, Canadian stock market, mutual funds Canada, ETF investing Canada, TFSA investing, RRSP strategies, financial planning Canada, wealth building Canada, personal finance Canada, investment courses online, Udemy investing courses, financial literacy Canada, retirement planning Canada, real estate investing Canada, cryptocurrency Canada, fintech education, portfolio management, CFA prep, financial advisor courses, securities license Canada, investment advisor training" />
        <meta name="author" content="Harry Vadalkar" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://harryvadalkar.ca" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://harryvadalkar.ca" />
        <meta property="og:title" content="Harry Vadalkar | Canadian Investing Courses | CSC & IFC Exam Prep" />
        <meta property="og:description" content="Master Canadian investing with expert courses. CSC exam prep, IFC certification, CIRO exam preparation. Author of Wealthy Canadian. 30,000+ students trained." />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_e36899ae-c2fa-440f-bf36-c9469e111964/artifacts/f8pxlvyc_HarryVadalkar-Website-4.jpg" />
        <meta property="og:site_name" content="Harry Vadalkar - Financial Education" />
        <meta property="og:locale" content="en_CA" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://harryvadalkar.ca" />
        <meta name="twitter:title" content="Harry Vadalkar | Canadian Investing & Exam Prep Courses" />
        <meta name="twitter:description" content="Expert Canadian investing courses. CSC exam prep, IFC course, CIRO certification. Wealthy Canadian author. Harvard & MIT certified." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_e36899ae-c2fa-440f-bf36-c9469e111964/artifacts/f8pxlvyc_HarryVadalkar-Website-4.jpg" />
        
        {/* Additional SEO */}
        <meta name="geo.region" content="CA-AB" />
        <meta name="geo.placename" content="Calgary" />
        <meta name="geo.position" content="51.0447;-114.0719" />
        <meta name="ICBM" content="51.0447, -114.0719" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      </Helmet>
      <div className="App dark">
        <BrowserRouter>
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/investors" element={<Investors />} />
              <Route path="/ifc" element={<IFC />} />
              <Route path="/ifc-csc" element={<IfcCsc />} />
              <Route path="/ciro" element={<Ciro />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/games" element={<Games />} />
              <Route path="/investment-academy" element={<InvestmentAcademy />} />
              <Route path="/news-ai-swarm" element={<NewsAISwarm />} />
              <Route path="/podcasts" element={<Podcasts />} />
              <Route path="/audiobooks" element={<AudioBooks />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/youtube" element={<YouTube />} />
              {/* Hidden page - not in navigation - for YouTube members only */}
              <Route path="/members/mutual-fund-book" element={<MutualFundBook />} />
            </Routes>
          </Layout>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;